// This brings in React and state management features needed for interactive UI.
import React, { useState } from 'react';
// This fetches the list of available form types for display.
import { getFormTypes } from '../../config/formTemplates';
// This utility will help log information and possible issues for easier monitoring.
import { logger } from "../../utils/logger";

// This component shows a popup for choosing which kind of form to create.
const FormTypeModal = ({ isOpen, onClose, onSelectFormType }) => {
  // This keeps track of which form type the user picks.
  const [selectedType, setSelectedType] = useState('');
  // Here, we get all possible types of forms the user can build.
  const formTypes = getFormTypes();

  // Log: Application started (for modal component initialization)
  logger.log("📂 [FormTypeModal] Modal component initialized");

  // This handles what happens when the user clicks "Create Form".
  const handleSubmit = () => {
    // Log: User is attempting to create a form with a selected type.
    logger.log("📂 [FormTypeModal] handleSubmit called with selectedType: " + selectedType);

    // If no type is selected, warn the user and stop further action.
    if (!selectedType) {
      // Log: Warn that the user did not select a form type.
      logger.warn("⚠️ [FormTypeModal] No form type selected – showing alert");
      alert('Please select a form type');
      return;
    }

    // Log: We are passing the chosen type back to the main app.
    logger.log("📂 [FormTypeModal] Calling onSelectFormType with: " + selectedType);

    try {
      onSelectFormType(selectedType);
      // Log: Success, passed control to parent component.
      logger.log("📂 [FormTypeModal] Selection sent to parent; parent handles closing");
    } catch (error) {
      // Log: Something failed while passing selection.
      logger.error("📂 [FormTypeModal] Application failed during selection: " + error.message);
    }

    // The parent component decides when to close the modal.
    // logger.log("📂 [FormTypeModal] NOT calling onClose - let parent handle it");
  };

  // If modal is not supposed to be shown, return nothing to hide UI.
  if (!isOpen) return null;

  // Renders the popup modal so the user can choose a form type.
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000
    }}>
      <div style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '16px',
        padding: '32px',
        maxWidth: '480px',
        width: '90%',
        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
        border: '1px solid #E5E7EB'
      }}>
        {/* This area shows the main title and instructions for the modal. */}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{
            fontSize: '48px',
            marginBottom: '16px'
          }}>📋</div>
          <h2 style={{
            margin: 0,
            fontSize: '24px',
            fontWeight: '700',
            color: '#111827',
            marginBottom: '8px'
          }}>
            Create Form Component
          </h2>
          <p style={{
            margin: 0,
            fontSize: '16px',
            color: '#6B7280',
            fontWeight: '500'
          }}>
            What is this form for?
          </p>
        </div>

        {/* This lets user pick a type for the new form. */}
        <div style={{ marginBottom: '32px' }}>
          <div style={{
            display: 'grid',
            gap: '12px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))'
          }}>
            {formTypes.map((type) => (
              <label
                key={type.value}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '16px',
                  border: `2px solid ${selectedType === type.value ? '#3B82F6' : '#E5E7EB'}`,
                  borderRadius: '12px',
                  cursor: 'pointer',
                  backgroundColor: selectedType === type.value ? '#EFF6FF' : '#FFFFFF',
                  transition: 'all 0.2s ease',
                  fontSize: '15px',
                  fontWeight: '500',
                  color: selectedType === type.value ? '#1D4ED8' : '#374151'
                }}
                // Highlight if hovered but not selected type.
                onMouseOver={(e) => {
                  if (selectedType !== type.value) {
                    e.target.style.borderColor = '#D1D5DB';
                    e.target.style.backgroundColor = '#F9FAFB';
                  }
                }}
                onMouseOut={(e) => {
                  if (selectedType !== type.value) {
                    e.target.style.borderColor = '#E5E7EB';
                    e.target.style.backgroundColor = '#FFFFFF';
                  }
                }}
              >
                <input
                  type="radio"
                  name="formType"
                  value={type.value}
                  checked={selectedType === type.value}
                  // Update selection whenever a new type is chosen.
                  onChange={(e) => {
                    setSelectedType(e.target.value);
                    // Log: User has selected a new form type.
                    logger.log("📂 [FormTypeModal] User selected form type: " + e.target.value);
                  }}
                  style={{
                    marginRight: '12px',
                    width: '18px',
                    height: '18px',
                    accentColor: '#3B82F6'
                  }}
                />
                <span>{type.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* This area provides the action buttons for the modal. */}
        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onClose}
            // User cancels out of the modal, nothing is selected.
            style={{
              padding: '12px 24px',
              fontSize: '15px',
              fontWeight: '600',
              color: '#6B7280',
              backgroundColor: '#F9FAFB',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            // Changes button appearance when hovered over.
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#F3F4F6';
              e.target.style.borderColor = '#D1D5DB';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#F9FAFB';
              e.target.style.borderColor = '#E5E7EB';
            }}
          >
            Cancel
          </button>
          <button
            // User confirms their choice, triggers handleSubmit for validation.
            onClick={handleSubmit}
            disabled={!selectedType}
            style={{
              padding: '12px 24px',
              fontSize: '15px',
              fontWeight: '600',
              color: '#FFFFFF',
              backgroundColor: selectedType ? '#3B82F6' : '#9CA3AF',
              border: 'none',
              borderRadius: '8px',
              cursor: selectedType ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s ease',
              minWidth: '120px'
            }}
            // Enhance UI feedback when hovered.
            onMouseOver={(e) => {
              if (selectedType) {
                e.target.style.backgroundColor = '#2563EB';
              }
            }}
            onMouseOut={(e) => {
              if (selectedType) {
                e.target.style.backgroundColor = '#3B82F6';
              }
            }}
          >
            Create Form
          </button>
        </div>
      </div>
    </div>
  );
};

// This lets other parts of the app use this modal component.
export default FormTypeModal;
